import { useLayoutEffect, useState, useTransition } from "react";
import baseStyles from "../BaseInputs.module.css";

interface ExpanderProps extends React.HTMLAttributes<HTMLDivElement> {
  lineChild: React.ReactNode;
  children: React.ReactNode;
  open?: boolean;
  timeMs?: number;
  disabled?: boolean;
  height?: number;
  contentBoxStyle?: React.CSSProperties;
}

type ExpanderState = "open" | "closing" | "closed" | "placing" | "opening";

/**
 * Expander control used to show or hide nested fields in the ObjectEditor.
 *
 * @param props - Destructured parameter object
 * @param props.lineChild - Node rendered in the header line for the expander (e.g. title or controls).
 * @param props.children - Content displayed inside the expander when open.
 * @param props.open - Initial open state; `true` opens the expander, `false` closes it.
 * @param props.timeMs - Animation duration in milliseconds for open/close transitions.
 * @param props.height - Maximum content height in pixels when the expander is open.
 * @param props.disabled - When true, user interaction is inhibited and the expander remains static.
 * @param props.contentBoxStyle - Inline styles applied to the inner content container when rendered.
 */
export const Expander = ({
  lineChild,
  children,
  open = false,
  timeMs = 300,
  height = 300,
  disabled = false,
  contentBoxStyle,
  ...rest
}: ExpanderProps) => {
  const [_isPending, startTransition] = useTransition();
  const [expanded, setExpanded] = useState<ExpanderState>("closed");
  const [lastOpen, setLastOpen] = useState<boolean | null>(null);
  useLayoutEffect(() => {
    if (open !== lastOpen) {
      if (open) {
        startTransition(() => setExpanded("placing"));
      } else if (lastOpen !== null) {
        startTransition(() => setExpanded("closing"));
      }
      startTransition(() => setLastOpen(open));
    }
  }, [expanded, open, lastOpen]);

  /**
   * Toggles the expanded state between "open" and "closed".
   *
   * @param e - Mouse event triggering the toggle action.
   */
  const toggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    switch (expanded) {
      case "closed":
        setExpanded("placing");
        break;
      case "open":
        setExpanded("closing");
        break;
    }
  };

  useLayoutEffect(() => {
    if (expanded === "placing") {
      setTimeout(() => {
        setExpanded("opening");
      }, 5);
    }
    if (expanded === "opening") {
      setTimeout(() => {
        setExpanded("open");
      }, timeMs);
    }
    if (expanded === "closing") {
      setTimeout(() => {
        setExpanded("closed");
      }, timeMs);
    }
  }, [expanded, timeMs]);

  return (
    <div
      {...rest}
      className={[
        baseStyles.expander,
        baseStyles[expanded],
        disabled ? baseStyles.disabled : "",
        ...(rest.className ?? ""),
      ].join(" ")}
      onClick={(e) => {
        if (
          !disabled &&
          e.target instanceof HTMLDivElement &&
          e.target.parentElement?.classList.contains(baseStyles.expander) &&
          e.target.classList.contains(baseStyles.holder)
        ) {
          toggle(e);
        }
      }}
    >
      <div className={baseStyles.holder}>{lineChild}</div>
      {expanded !== "closed" && (
        <div
          className={baseStyles.expanderContent}
          style={{
            ...contentBoxStyle,
            transition: `min-height ${timeMs}ms ease-in-out, max-height ${timeMs}ms ease-in-out, opacity ${timeMs}ms ease-in-out`,
            maxHeight: ["open", "opening"].includes(expanded) ? `${height}px` : "0",
            minHeight: ["open", "opening"].includes(expanded) ? `${height}px` : "0",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

Expander.displayName = "Expander";
